import React from 'react';

import { removeProjectDescriptionImage } from '~src/projects/components/ProjectTooltip';
import { CardViewStyles } from '~src/spacesPlatform/components/card/CardViewStyles';
import { DetailsTooltipStyles } from '~src/spacesPlatform/components/card/DetailsTooltipStyles';

import { Messages } from '../../../codegen/Localize';
import { L10n } from '../../../commonInfra/L10n/L10n';
import { Name } from '../../../spacesPlatform/components/tooltip/Name';
import { Timestamp } from '../../../spacesPlatform/components/tooltip/Timestamp';
import { Tooltip } from '../../../spacesPlatform/components/tooltip/Tooltip';
import { ContentTooltipProps, TooltipDescriptionTestId } from '../../../spacesPlatform/components/tooltip/TooltipHelper';
import { User } from '../../../spacesPlatform/components/tooltip/User';
import { ISharedWithMeContentInfo, ISharedWithMeCustomViewInfo } from '../../models/ISharedWithMeContentInfo';
import { renderSharerNameWithMessage, renderSharerProfilePicture } from '../cardView/SharedWithMeCard';

export const MessageFromSharerTestId = 'tooltip-message-from-sharer';
export const OwnerTestId = 'tooltip-owner';
export const DescriptionCharLimit = 100;
export const MessageCharLimit = 225;

export function SharedWithMeTooltip(props: ContentTooltipProps): JSX.Element {
  const content = props.content as ISharedWithMeContentInfo;
  return (
    <Tooltip>
      <Name name={content.name} url={props.contentUrl}/>
      <div className={DetailsTooltipStyles.SharedWithMeContentDescription} data-tb-test-id={TooltipDescriptionTestId}>
        {contentDescription(content)}
      </div>
      <div className={DetailsTooltipStyles.SharedWithMeMetadata}>
        {renderSharerProfilePicture(content.sharer)}
        <div className={CardViewStyles.SharedWithMeSharerAndText}>
          <div>
            {renderSharerNameWithMessage(
              /** sharer */ content.sharer,
              /** urls */ props.urls,
              /** message */ mapContentTypeToTooltipString(content.contentType, content.customView),
              /** shownBelowThumbnail */ false,
              /** cv */ content.customView,
            )
            }
          </div>
          <div data-tb-test-id={MessageFromSharerTestId}>
            {content.message ? Messages.sharedWithMeMessage({MESSAGE: L10n.elideIfLong(content.message, MessageCharLimit)}) : ''}
          </div>
        </div>
      </div>
      <dl className={DetailsTooltipStyles.DescriptionList}>
        <User data-tb-test-id={OwnerTestId} user={content.owner} label={Messages.owner()} urls={props.urls}/>
        <Timestamp label={Messages.modified()} value={content.updatedAt}/>
      </dl>
    </Tooltip>
);
}

function contentDescription(content: ISharedWithMeContentInfo): string {
  const fullDescription = content.contentType.toLowerCase() === 'project'
    ? removeProjectDescriptionImage(content.description)
    : content.description;
  return L10n.elideIfLong(fullDescription, DescriptionCharLimit);
}

function mapContentTypeToTooltipString(contentType: string, customView?: ISharedWithMeCustomViewInfo): string {
  switch (contentType.toLowerCase()) {
    case 'view':
      // if the content has an associated custom view, use the string for custom/dirty view instead
      if (customView) {
        if (customView.isHidden) {
          return Messages.sharedWithMeTooltipForDirtyView();
        } else {
          return Messages.sharedWithMeTooltipForCustomView();
        }
      }
      return Messages.sharedWithMeTooltipForView();
    case 'project':
      return Messages.sharedWithMeTooltipForProject();
    case 'workbook':
      return Messages.sharedWithMeTooltipForWorkbook();
    case 'datasource':
      return Messages.sharedWithMeTooltipForDatasource();
    case 'flow':
      return Messages.sharedWithMeTooltipForFlow();
    case 'metric':
      return Messages.sharedWithMeTooltipForMetric();
    case 'virtualconnection':
      return Messages.sharedWithMeTooltipForPublishedConnection();
    case 'collection':
      return Messages.sharedWithMeTooltipForCollection();
    default:
      return Messages.shared();
  }
}
